import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {TASK_WITHOUT_RESPONSIBLE_PARTIES_TEXT} from 'processes/ProcessConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import Modal from 'ui-library/modals/Modal';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

import {ProcessTaskListingGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';

interface ProcessTaskResponsiblePartiesEditorModalProps {
  onRequestSubmit: (responsibleParties: Array<EntityWithTextAndSuspended>) => void;
  onRequestClose: () => void;
  items: Array<ProcessTaskListingGraphQL>;
}

function getCurrentResponsibleParties(items: Array<ProcessTaskListingGraphQL>): string {
  const existingResponsiblePartiesIds = items[0].responsibleParties.map(
    (responsibleParty) => responsibleParty.id
  );
  const allSameResponsibleParties = items.every((task) =>
    isEqual(
      task.responsibleParties.map((responsibleParty) => responsibleParty.id),
      existingResponsiblePartiesIds
    )
  );
  const responsiblePartyHandles = items[0].responsibleParties.map(
    (responsibleParty) => responsibleParty.handle
  );
  const responsiblePartiesText =
    responsiblePartyHandles.length === 0
      ? TASK_WITHOUT_RESPONSIBLE_PARTIES_TEXT
      : responsiblePartyHandles.join(', ');
  return allSameResponsibleParties ? responsiblePartiesText : 'Multiple responsible parties';
}

export default function ProcessTaskResponsiblePartiesEditorModal(
  props: ProcessTaskResponsiblePartiesEditorModalProps
): JSX.Element {
  assert(
    props.items.length > 0,
    'ProcessTaskResponsiblePartiesEditorModal requires at least one selected item'
  );
  const [responsibleParties, setResponsibleParties] = useState<Array<EntityWithTextAndSuspended>>([]);
  return (
    <Modal
      title="Bulk update responsible parties"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(responsibleParties);
      }}
      buttons={[
        {
          text: 'Update',
          submit: true,
          dataTestAttrs: {component: 'ProcessTaskResponsiblePartiesEditorModal', key: ModalButtonKey.UPDATE},
        },
      ]}
    >
      <div className={css(styles.container)}>
        <span className={css(styles.field)}>
          <span className={css(styles.label)}>Current Value:</span>
          <span className={css(styles.current)}>
            <div data-test="ProcessTaskResponsiblePartiesEditor-responsiblePartiesHandle">
              {getCurrentResponsibleParties(props.items)}
            </div>
          </span>
        </span>
        <span className={css(styles.field)}>
          <span className={css(styles.label)}>New Value:</span>
          <EntityInputWithSearch
            value={responsibleParties}
            placeholder="Leave blank to unassign"
            size={TagsInputSize.SMALL}
            onChange={setResponsibleParties}
            allowEmails={true}
            allowTeams={true}
            allowOrganizations={false}
            shouldAllowMultiple={true}
            enableNewEntityInput={true}
            emptyText="No results found"
          />
        </span>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
  },
  field: {
    flexGrow: 1,
    width: '50%',
    marginRight: 16,
  },
  label: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  current: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
  },
});
